import { Component, inject } from '@angular/core';
import { SharedModule, FilterMatchMode } from 'primeng/api';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { DropdownModule } from 'primeng/dropdown';
import { ToolService } from '../../../service/tool.service';
import { TreeHelperService } from '../../../service/helper/tree-helper.service';
import { WimTreeNode } from '../../../model/wim-tree.node';
import { NodeType } from '../../../constants/node-type';
import { ToolListItem } from '../../../model/tool-list-item.model';
import { WimIcons } from '../../../constants/wim-icons';
import { GridRequest } from '../../../model/grid-request.model';
import { CantaaErrorHandlerService } from '../../../service/cantaa-error-handler.service';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { RouteType } from '../../../constants/route-type';
import { CantaaMessageService } from '../../../service/cantaa-message.service';
import { ToolDocumentService } from "../../../service/tool-document.service";
import { Permission } from '../../../constants/Permission';
import { HasPermissionDirective } from '../../../directive/has-permission.directive';
import { TranslateModule } from "@ngx-translate/core";
import { UuidHelperService } from '../../../service/helper/uuid-helper.service';
import { BaseNodeGridComponent } from '../../base-components/base-node-grid.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { SearchInputComponent } from '../../inputs/search-input/search-input.component';
import { SystemSettingsService } from '../../../service/system-settings.service';
import { environment } from '../../../../environments/environment';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'wim-tool-grid',
  standalone: true,
  imports: [
    SharedModule,
    TableModule,
    DropdownModule,
    MatButton,
    MatIcon,
    HasPermissionDirective,
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    SearchInputComponent,
    CommonModule
  ],
  templateUrl: './tool-grid.component.html',
  styleUrl: './tool-grid.component.scss'
})
export class ToolGrid extends BaseNodeGridComponent<ToolListItem> {

  protected readonly Permission = Permission;
  protected override nodeTypes = [NodeType.TOOL_RELATION];

  private toolService = inject(ToolService);
  private treeHelperService = inject(TreeHelperService);
  private errorHandler = inject(CantaaErrorHandlerService);
  private messageService = inject(CantaaMessageService);

  public toolDocumentService = inject(ToolDocumentService);

  private selectedNode!: WimTreeNode;
  public search4ToolIdEnabled = false;
  public settingsLoaded = false;

  private systemSettingsService = inject(SystemSettingsService);

  override async ngAfterViewInit() {
    this.initDefaultFilters();
    await this.loadSystemSettings();
    await super.ngAfterViewInit();
  }

  private initDefaultFilters(): void {
    if (!this.gridMeta.filters) {
      this.gridMeta.filters = {};
    }

    if (!this.gridMeta.filters['status']) {
      this.gridMeta.filters['status'] = [
        {
          value: 'ACTIVE',
          matchMode: FilterMatchMode.EQUALS,
          operator: 'and'
        }
      ];
    }
  }

  private async loadSystemSettings() {
    try {
      const settings = await this.systemSettingsService.getSystemSettings();
      this.search4ToolIdEnabled = settings.search4toolId || environment.debugMode;
    } catch (e) {
      this.search4ToolIdEnabled = environment.debugMode;
    } finally {
      this.settingsLoaded = true;
      this.cdRef.markForCheck();
    }
  }

  override async fetchData(selectedNode: WimTreeNode, lazyLoadMetadata: TableLazyLoadEvent) {
    // Auswahl zurücksetzen, wenn sich die Tabellendaten neu laden (z. B. durch Filterwechsel)
    this.selectedItem = null;

    await this.loadSystemSettings();
    this.selectedNode = selectedNode;
    await super.fetchData(selectedNode, lazyLoadMetadata);
    this.gridItems.forEach(tool => {
      this.toolDocumentService.getDefaultPhotoScaled35x35AndRender(tool.id);
    });
  }

  override async fetchGridItems(gridRequest: GridRequest, selectedNode: WimTreeNode) {
    try {
      return await this.toolService.getTools(gridRequest, selectedNode);
    } catch (e) {
      this.errorHandler.handleError(e, 'FAILED_TO_FETCH');
      throw e;
    }
  }

  override mapTreeItems(selectedNode: WimTreeNode, items: ToolListItem[]): WimTreeNode[] {
    return items
      .map<WimTreeNode>(t => {

        let treeNode = {
          id: t.id,
          key: UuidHelperService.generateUuid(),
          label: (t.category !== null && t.category !== undefined && t.category.trim() !== '') ? `${t.category} ${t.productName}` : t.productName,
          data: t.category,
          icon: WimIcons.TOOL_ICON,
          nodeType: NodeType.TOOL,
          route: RouteType.TOOL_ALL,
          children: [] as WimTreeNode[],
          leaf: false,
          parent: selectedNode
        };

        t.treeNode = treeNode;

        treeNode.children = [
          this.treeHelperService.createToolLocationRelationsTreeItem(treeNode),
          this.treeHelperService.createTransactionRelationsTreeItem(treeNode),
          this.treeHelperService.createProjectRelationsTreeItem(treeNode)
        ];

        return treeNode;
      });
  }

  async delete() {
    try {
      await this.toolService.deleteTool(this.selectedItem!);
      this.messageService.success('DELETE.SUCCESS');
      await this.reFetchData();
    } catch (e) {
      throw this.errorHandler.handleError(e, 'DELETE.FAILED');
    }
  }

  async activate() {
    try {
      await this.toolService.activateTool(this.selectedItem!);
      this.messageService.success('ACTIVATE_SUCCESS');
      await this.reFetchData();
    } catch (e) {
      throw this.errorHandler.handleError(e, 'ACTIVATE_FAILED');
    }
  }

  async export(table: Table) {
    const filterDefinitions = this.gridHelperService.mapFilterDefinitions(table.filters);
    if (this.selectedNode.parent) {
      const contextNode = this.selectedNode.parent as WimTreeNode
      if (contextNode.id)
        filterDefinitions.push({ propertyName: "contextId", value: String(contextNode.id) });
      filterDefinitions.push({ propertyName: "contextRoute", value: contextNode.route });
    } else {
      filterDefinitions.push({ propertyName: "contextRoute", value: this.selectedNode.route });
    }
    const gridRequest: GridRequest = {
      pageRequest: {},
      filterDefinitions: filterDefinitions,
      sortDefinitions: this.gridHelperService.mapSortDefinitions(table.multiSortMeta),
      searchQuery: this.gridMeta.searchQuery
    };
    try {
      await this.toolService.exportTools(gridRequest, 'tools-export.xlsx');
    } catch (error) {
      throw this.errorHandler.handleError(error, 'EXPORT.FAILED');
    }
  }

  getRowClass(tool: ToolListItem): string {
    if (tool.status === 'DELETED') {
      return 'status-deleted';
    }
    return '';
  }

}
